import { useCallback, useEffect, useMemo, useState } from 'react';

import { getAccessTokenFromCookie } from '../../services/auth';
import { get, getToJson, post, postToJson } from '../utils/io';

type UseWebDataLoaderParams = {
  url?: string | URL;
  content?: string;
  isPlain?: boolean;
  page?: number;
  pageSize?: number;
};

type WebDataLoaderData<T = unknown> = {
  loading: boolean;
  error: unknown;
  data: T | undefined;
};

export default function useWebDataLoader<T>({
  url = '',
  content,
  isPlain = false,
  page,
  pageSize,
}: UseWebDataLoaderParams): [WebDataLoaderData<T>, () => (() => void) | undefined] {
  const fns: {
    get: (url: string | URL, token: string | null) => Promise<T>;
    post: (url: string | URL, content: string, token: string | null) => Promise<T>;
  } = useMemo(
    () => ({
      get: (isPlain ? async (_url: string, token: string) => (await get(_url, token)).text() : getToJson) as unknown as (
        url: string | URL,
        token: string | null
      ) => Promise<T>,
      post: (isPlain
        ? async (_url: string, _content: string, token: string) => (await post(_url, _content, token)).text()
        : postToJson) as unknown as (url: string | URL, content: string, token: string | null) => Promise<T>,
    }),
    [isPlain]
  );

  const [data, setData] = useState<WebDataLoaderData<T>>({
    loading: false,
    error: null,
    data: undefined,
  });

  const runUpdate = useCallback(() => {
    let cancelStateUpdate = false;
    if (!url) {
      setData((d) => ({ ...d, loading: false, data: undefined }));
      return;
    }

    // build URL with pagination params if present
    const pagedUrl = new URL(url);
    if (page !== undefined && pageSize !== undefined) {
      const offset = (page - 1) * pageSize;
      const limit = pageSize;
      pagedUrl.searchParams.set('offset', String(offset));
      pagedUrl.searchParams.set('limit', String(limit));
    }

    setData((d) => ({ ...d, loading: true }));
    void (async () => {
      try {
        if (!url) {
          setData((d) => ({ ...d, loading: false }));
          return;
        }

        const authToken = getAccessTokenFromCookie()!;

        const isPost = content !== undefined;
        const loadedData = isPost ? await fns.post(pagedUrl, content, authToken) : await fns.get(pagedUrl, authToken);

        if (cancelStateUpdate) {
          return;
        }

        setData({
          loading: false,
          error: null,
          data: loadedData,
        });
      } catch (err: unknown) {
        console.error('[useDataLoader] failed to load data...', err);
        setData({ error: err, loading: false, data: [] as T });
      }
    })();

    return () => {
      cancelStateUpdate = true;
    };
  }, [fns, url, content, page, pageSize]);

  useEffect(runUpdate, [runUpdate, url, content, page, pageSize]);

  return [data, runUpdate];
}
